import React from 'react';
import { Bot, Workflow, Database, MessageSquare, BarChart3, Phone, ArrowUpRight, Building2 } from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import { Reveal, RevealGroup, RevealItem } from './ui/Reveal';
import { REAL_ESTATE_PATH } from '../lib/router';

interface Service {
    icon: React.ElementType;
    title: string;
    description: string;
    accent: string; // text color
    glow: string; // gradient for hover glow
    featured?: boolean;
    tags?: string[];
}

const services: Service[] = [
    {
        icon: Bot,
        title: 'AI Agents & Assistants',
        description:
            'Custom AI agents that handle support, qualify leads, process documents, and answer internal questions 24/7 — trained on your data and tone of voice.',
        accent: 'text-brand-cyan',
        glow: 'from-brand-cyan/20',
        featured: true,
        tags: ['GPT-4 / Claude', 'RAG', 'Voice AI', 'Fine-tuning'],
    },
    {
        icon: Workflow,
        title: 'Workflow Engineering',
        description: 'End-to-end process automation with n8n, Make & custom code that removes manual steps entirely.',
        accent: 'text-brand-blue',
        glow: 'from-brand-blue/20',
    },
    {
        icon: Database,
        title: 'CRM & API Integrations',
        description: 'Connect your CRM, marketing, billing, and ops tools into one clean, reliable source of truth.',
        accent: 'text-brand-indigo',
        glow: 'from-brand-indigo/20',
    },
    {
        icon: MessageSquare,
        title: 'WhatsApp & Chat Automation',
        description: 'Smart bots for lead capture, order updates, and support — with seamless human hand-off.',
        accent: 'text-emerald-400',
        glow: 'from-emerald-400/20',
    },
    {
        icon: Phone,
        title: 'AI Voice Call Agents',
        description: 'Human-sounding agents that answer calls, book appointments, and take orders around the clock.',
        accent: 'text-brand-orange',
        glow: 'from-brand-orange/20',
    },
    {
        icon: BarChart3,
        title: 'Reporting & BI Dashboards',
        description: 'Automated dashboards that pull every data source into clear, real-time, decision-ready insight.',
        accent: 'text-purple-400',
        glow: 'from-purple-400/20',
    },
];

const ServiceCard: React.FC<{ service: Service }> = ({ service }) => {
    const Icon = service.icon;
    return (
        <RevealItem className={service.featured ? 'md:col-span-2 md:row-span-2' : ''}>
            <div className="group relative h-full overflow-hidden rounded-2xl card-surface p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:border-white/[0.14]">
                {/* hover glow */}
                <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br ${service.glow} to-transparent blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
                {/* top hairline on hover */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="relative flex items-start justify-between">
                    <div className={`w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.06] grid place-items-center ${service.accent} group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-7 h-7" strokeWidth={1.75} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-text-faint opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all" />
                </div>

                <h3 className={`relative mt-6 font-display font-bold text-white ${service.featured ? 'text-2xl' : 'text-xl'}`}>
                    {service.title}
                </h3>
                <p className={`relative mt-3 text-text-muted leading-relaxed ${service.featured ? 'text-base md:text-lg max-w-md' : 'text-sm'}`}>
                    {service.description}
                </p>

                {service.tags && (
                    <div className="relative mt-6 flex flex-wrap gap-2">
                        {service.tags.map((tag) => (
                            <span key={tag} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/[0.04] text-text-muted border border-white/[0.06]">
                                {tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </RevealItem>
    );
};

const Services: React.FC = () => {
    return (
        <section id="services" className="relative py-24 md:py-32">
            <div className="container-x">
                <SectionHeading
                    eyebrow="What we do"
                    title={<>Automation that thinks, <span className="text-gradient">acts, and scales</span></>}
                    subtitle="We don't just connect apps. We build intelligent systems that make decisions, take action, and learn — plugging straight into how your business already works."
                />

                <RevealGroup className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
                    {services.map((service) => (
                        <ServiceCard key={service.title} service={service} />
                    ))}
                </RevealGroup>

                <Reveal className="mt-5">
                    <a
                        href={REAL_ESTATE_PATH}
                        className="group relative flex items-center gap-4 sm:gap-6 overflow-hidden rounded-2xl card-surface p-5 sm:p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-cyan/30"
                    >
                        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-brand-cyan/[0.07] to-transparent pointer-events-none" />
                        <div className="relative w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-2xl bg-brand-cyan/10 border border-brand-cyan/20 grid place-items-center text-brand-cyan">
                            <Building2 className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.75} />
                        </div>
                        <div className="relative min-w-0 flex-1">
                            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-cyan">
                                Real estate demo
                            </span>
                            <p className="mt-1 font-display font-bold text-white text-base sm:text-lg leading-snug">
                                Watch our AI agent call a buyer within 2 minutes.
                            </p>
                        </div>
                        <ArrowUpRight className="relative w-5 h-5 shrink-0 text-text-muted group-hover:text-brand-cyan group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </a>
                </Reveal>
            </div>
        </section>
    );
};

export default Services;
